/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\AgentAiTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Integrations Config module.
----------------------------------------------------------------*/

import React from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { FormBox } from '../../../components/ui/FormBox';
import Toggle from '../../../components/ui/Toggle';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Button } from '../../../components/ui/button';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import ModuleDestinationSelect from '../../../components/ui/ModuleDestinationSelect';
import Modal from '../../../components/ui/Modal';
import { Bot, Settings, TestTube, History, CheckCircle, XCircle, Clock, Info, Code } from 'lucide-react';

interface AiToolSettings {
    ai_enabled: boolean;
    ai_allowed_actions: string[];
    ai_policies: {
        require_confirmation_for_writes: boolean;
        max_actions_per_conversation: number;
        timeout_ms: number;
        retry_attempts: number;
        redact_logs: boolean;
        fallback_behavior: 'continue' | 'escalate' | 'fail';
        escalation_destination_type?: string;  // Module type for escalation (extension, queue, etc.)
        escalation_destination?: string;       // Destination value when fallback is 'escalate'
    };
    // Calendly-specific settings
    email_template_id?: string;   // Email template for send_booking_email
    sms_number_id?: string;       // SMS number for send_booking_sms
}

interface ToolLog {
    id: string;
    action: string;
    status: 'SUCCESS' | 'ERROR' | 'TIMEOUT';
    latencyMs: number;
    error?: string;
    createdAt: string;
}

interface AgentAiTabProps {
    provider: string;
    settings: AiToolSettings;
    onSettingsChange: (settings: AiToolSettings) => void;
    toolsSchema?: string;  // JSON string of OpenAI tool definitions
    onToolsSchemaChange?: (schema: string) => void;
    toolLogs?: ToolLog[];
    onTestAction?: (action: string) => Promise<void>;
    isTesting?: boolean;
}

// CRM actions for CRM providers
const CRM_ACTIONS = [
    'search_contact',
    'get_contact',
    'add_note',
    'log_call',
    'create_lead',
    'update_contact'
];

// Calendly actions - Direct booking via POST /invitees API
const CALENDLY_ACTIONS = [
    'get_event_types',        // GET /event_types - List available event types
    'get_available_times',    // GET /event_type_available_times - Get available slots
    'create_booking',         // POST /invitees - Create booking directly (collects name, email, timezone)
    'list_scheduled_events',  // GET /scheduled_events - List upcoming events
    'cancel_event'            // DELETE /scheduled_events - Cancel an event
];

const DEFAULT_POLICIES: AiToolSettings['ai_policies'] = {
    require_confirmation_for_writes: true,
    max_actions_per_conversation: 10,
    timeout_ms: 5000,
    retry_attempts: 2,
    redact_logs: true,
    fallback_behavior: 'continue'
};

// Default tool schemas per provider
const getDefaultToolsSchema = (provider: string): object[] => {
    const providerLower = provider.toLowerCase();

    if (providerLower === 'calendly') {
        return [
            {
                type: "function",
                name: "check_availability",
                description: "Check available time slots for scheduling an appointment. Call this when the user wants to schedule a meeting or appointment.",
                parameters: {
                    type: "object",
                    properties: {
                        date: {
                            type: "string",
                            description: "The date to check availability for in YYYY-MM-DD format"
                        },
                        duration_minutes: {
                            type: "integer",
                            description: "Duration of the appointment in minutes. Default is 30."
                        }
                    },
                    required: ["date"]
                }
            },
            {
                type: "function",
                name: "schedule_appointment",
                description: "Schedule an appointment at a specific time slot. Only call this after confirming the time with the user.",
                parameters: {
                    type: "object",
                    properties: {
                        start_time: {
                            type: "string",
                            description: "The start time in ISO 8601 format (e.g., 2026-01-25T10:00:00-06:00)"
                        },
                        invitee_name: {
                            type: "string",
                            description: "Full name of the person scheduling the appointment"
                        },
                        invitee_email: {
                            type: "string",
                            description: "Email address for sending confirmation"
                        },
                        notes: {
                            type: "string",
                            description: "Optional notes or reason for the appointment"
                        }
                    },
                    required: ["start_time", "invitee_name", "invitee_email"]
                }
            },
            {
                type: "function",
                name: "cancel_appointment",
                description: "Cancel an existing appointment. Ask for confirmation before canceling.",
                parameters: {
                    type: "object",
                    properties: {
                        invitee_email: {
                            type: "string",
                            description: "Email address used when booking the appointment"
                        },
                        reason: {
                            type: "string",
                            description: "Reason for cancellation"
                        }
                    },
                    required: ["invitee_email"]
                }
            }
        ];
    }

    // CRM default tools
    return [
        {
            type: "function",
            name: "search_contact",
            description: "Search for a contact in the CRM by name, email, or phone number.",
            parameters: {
                type: "object",
                properties: {
                    query: {
                        type: "string",
                        description: "Search query (name, email, or phone)"
                    }
                },
                required: ["query"]
            }
        }
    ];
};

export const AgentAiTab: React.FC<AgentAiTabProps> = ({
    provider,
    settings,
    onSettingsChange,
    toolsSchema = '',
    onToolsSchemaChange,
    toolLogs = [],
    onTestAction,
    isTesting = false
}) => {
    const { t } = useI18n();
    const [jsonError, setJsonError] = React.useState<string | null>(null);
    const [showToolsModal, setShowToolsModal] = React.useState(false);

    // Check if this is Calendly provider
    const isCalendly = provider === 'calendly';

    // Determine available actions based on provider
    const availableActions = isCalendly ? CALENDLY_ACTIONS : CRM_ACTIONS;

    // Ensure settings have default values
    const currentSettings: AiToolSettings = {
        ai_enabled: settings.ai_enabled || false,
        ai_allowed_actions: settings.ai_allowed_actions || [],
        ai_policies: { ...DEFAULT_POLICIES, ...settings.ai_policies }
    };

    const handleToggleAction = (action: string) => {
        const actions = currentSettings.ai_allowed_actions.includes(action)
            ? currentSettings.ai_allowed_actions.filter(a => a !== action)
            : [...currentSettings.ai_allowed_actions, action];
        onSettingsChange({ ...currentSettings, ai_allowed_actions: actions });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handlePolicyChange = (key: keyof AiToolSettings['ai_policies'], value: any) => {
        onSettingsChange({
            ...currentSettings,
            ai_policies: { ...currentSettings.ai_policies, [key]: value }
        });
    };

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'SUCCESS':
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'ERROR':
                return <XCircle className="h-4 w-4 text-red-500" />;
            case 'TIMEOUT':
                return <Clock className="h-4 w-4 text-yellow-500" />;
            default:
                return null;
        }
    };

    return (
        <div className="space-y-6">
            {/* Enable AI Access */}
            <FormBox
                title={t('integrations.agentAi.title', 'Agent AI Tools')}
                icon={<Bot className="h-5 w-5" />}
            >
                <div className="p-6 space-y-3">
                    {/* Enable Toggle + JSON Tools in grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                        {/* Enable Agent AI Access */}
                        <Label className="text-left">
                            {t('integrations.agentAi.enabled', 'Enable Agent AI Access')}
                        </Label>
                        <Toggle
                            value={currentSettings.ai_enabled}
                            onChange={(val) => onSettingsChange({ ...currentSettings, ai_enabled: val })}
                        />

                        {/* JSON Tools Definition */}
                        {onToolsSchemaChange && currentSettings.ai_enabled && (
                            <>
                                <Label className="text-left">
                                    {t('integrations.agentAi.toolsSchema', 'JSON Tools Definition')}
                                </Label>
                                <button
                                    onClick={() => setShowToolsModal(true)}
                                    className="flex items-center gap-2 px-3 py-2 text-sm text-left border border-input rounded-md bg-background hover:bg-accent transition-colors"
                                >
                                    <Code className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-muted-foreground truncate">
                                        {toolsSchema ? t('integrations.agentAi.clickToEdit', 'Click to edit...') : t('integrations.agentAi.clickToConfigure', 'Click to configure...')}
                                    </span>
                                </button>
                            </>
                        )}
                    </div>

                    {currentSettings.ai_enabled && (
                        <>
                            {/* Tools Schema Modal */}
                            {onToolsSchemaChange && (
                                <Modal
                                    isOpen={showToolsModal}
                                    onClose={() => setShowToolsModal(false)}
                                    title={t('integrations.agentAi.toolsSchema', 'OpenAI Tool Definitions')}
                                    size="lg"
                                >
                                    <div className="space-y-4">
                                        <div className="flex gap-2 justify-end">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    const defaults = getDefaultToolsSchema(provider);
                                                    onToolsSchemaChange(JSON.stringify(defaults, null, 2));
                                                    setJsonError(null);
                                                }}
                                            >
                                                {t('integrations.agentAi.loadDefaults', 'Load Defaults')}
                                            </Button>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => {
                                                    try {
                                                        if (toolsSchema) {
                                                            JSON.parse(toolsSchema);
                                                            setJsonError(null);
                                                        }
                                                    } catch (err) {
                                                        setJsonError(err instanceof Error ? err.message : 'Invalid JSON');
                                                    }
                                                }}
                                            >
                                                {t('common.validate', 'Validate')}
                                            </Button>
                                        </div>
                                        <textarea
                                            value={toolsSchema}
                                            onChange={(e) => {
                                                onToolsSchemaChange(e.target.value);
                                                setJsonError(null);
                                            }}
                                            placeholder='[\n  {\n    "type": "function",\n    "name": "tool_name",\n    "description": "What this tool does",\n    "parameters": { ... }\n  }\n]'
                                            className="w-full h-80 font-mono text-sm p-3 rounded-md border border-input bg-background resize-y"
                                        />
                                        {jsonError && (
                                            <p className="text-sm text-red-500 flex items-center gap-1">
                                                <XCircle className="h-4 w-4" />
                                                {jsonError}
                                            </p>
                                        )}
                                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Info className="h-3 w-3" />
                                            {t('integrations.agentAi.toolsSchemaHelp', 'Define OpenAI-compatible function tools that AI agents can use with this integration.')}
                                        </p>
                                    </div>
                                </Modal>
                            )}

                            {/* Allowed Actions */}
                            <div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center">
                                    {availableActions.map(action => (
                                        <React.Fragment key={action}>
                                            <SimpleTooltip content={t(`integrations.agentAi.actions.${action}Help`, '')}>
                                                <Label className="text-left">
                                                    {t(`integrations.agentAi.actions.${action}`, action.replace(/_/g, ' '))}
                                                </Label>
                                            </SimpleTooltip>
                                            <Toggle
                                                value={currentSettings.ai_allowed_actions.includes(action)}
                                                onChange={() => handleToggleAction(action)}
                                            />
                                        </React.Fragment>
                                    ))}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </FormBox>

            {/* Policies */}
            {currentSettings.ai_enabled && (
                <FormBox
                    title={t('integrations.agentAi.policies', 'Policies')}
                    icon={<Settings className="h-5 w-5" />}
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 items-center p-6">
                        {/* Row 1: Require Confirmation | Toggle | Redact Logs | Toggle */}
                        <SimpleTooltip content={t('integrations.agentAi.policyFields.requireConfirmationHelp')}>
                            <Label className="text-left">{t('integrations.agentAi.policyFields.requireConfirmation', 'Require confirmation for writes')}</Label>
                        </SimpleTooltip>
                        <Toggle
                            value={currentSettings.ai_policies.require_confirmation_for_writes}
                            onChange={(val) => handlePolicyChange('require_confirmation_for_writes', val)}
                        />

                        <SimpleTooltip content={t('integrations.agentAi.policyFields.redactLogsHelp')}>
                            <Label className="text-left">{t('integrations.agentAi.policyFields.redactLogs', 'Redact sensitive data in logs')}</Label>
                        </SimpleTooltip>
                        <Toggle
                            value={currentSettings.ai_policies.redact_logs}
                            onChange={(val) => handlePolicyChange('redact_logs', val)}
                        />

                        {/* Row 2: Max Actions | Input | Timeout | Input */}
                        <SimpleTooltip content={t('integrations.agentAi.policyFields.maxActionsHelp')}>
                            <Label className="text-left">{t('integrations.agentAi.policyFields.maxActions', 'Max actions per conversation')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={currentSettings.ai_policies.max_actions_per_conversation}
                            onChange={(e) => handlePolicyChange('max_actions_per_conversation', parseInt(e.target.value) || 10)}
                            min={1}
                            max={50}
                        />

                        <SimpleTooltip content={t('integrations.agentAi.policyFields.timeoutHelp')}>
                            <Label className="text-left">{t('integrations.agentAi.policyFields.timeout', 'Timeout (ms)')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={currentSettings.ai_policies.timeout_ms}
                            onChange={(e) => handlePolicyChange('timeout_ms', parseInt(e.target.value) || 5000)}
                            min={1000}
                            max={30000}
                            step={1000}
                        />

                        {/* Row 3: Retry Attempts | Input | Fallback Behavior | Select */}
                        <SimpleTooltip content={t('integrations.agentAi.policyFields.retryAttemptsHelp')}>
                            <Label className="text-left">{t('integrations.agentAi.policyFields.retryAttempts', 'Retry attempts')}</Label>
                        </SimpleTooltip>
                        <Input
                            type="number"
                            value={currentSettings.ai_policies.retry_attempts}
                            onChange={(e) => handlePolicyChange('retry_attempts', parseInt(e.target.value) || 2)}
                            min={0}
                            max={5}
                        />

                        <SimpleTooltip content={t('integrations.agentAi.policyFields.fallbackBehaviorHelp')}>
                            <Label className="text-left">{t('integrations.agentAi.policyFields.fallbackBehavior', 'Fallback behavior')}</Label>
                        </SimpleTooltip>
                        <select
                            value={currentSettings.ai_policies.fallback_behavior}
                            onChange={(e) => handlePolicyChange('fallback_behavior', e.target.value)}
                            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                        >
                            <option value="continue">{t('integrations.agentAi.policyFields.fallbackOptions.continue', 'Continue conversation')}</option>
                            <option value="escalate">{t('integrations.agentAi.policyFields.fallbackOptions.escalate', 'Escalate to human')}</option>
                            <option value="fail">{t('integrations.agentAi.policyFields.fallbackOptions.fail', 'End interaction')}</option>
                        </select>

                        {/* Escalation Destination - only shown when fallback is 'escalate' */}
                        {currentSettings.ai_policies.fallback_behavior === 'escalate' && (
                            <>
                                <SimpleTooltip content={t('integrations.agentAi.policyFields.escalationDestinationHelp', 'Destination to transfer the call when escalating to a human agent')}>
                                    <Label className="text-left">{t('integrations.agentAi.policyFields.escalationDestination', 'Escalation Destination')}</Label>
                                </SimpleTooltip>
                                <div className="col-span-3">
                                    <ModuleDestinationSelect
                                        moduleValue={currentSettings.ai_policies.escalation_destination_type || ''}
                                        destinationValue={currentSettings.ai_policies.escalation_destination || ''}
                                        onModuleChange={(value) => handlePolicyChange('escalation_destination_type', value)}
                                        onDestinationChange={(value) => handlePolicyChange('escalation_destination', value)}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </FormBox>
            )}

            {/* Test Actions */}
            {currentSettings.ai_enabled && onTestAction && (
                <FormBox
                    title={t('integrations.agentAi.testActions', 'Test Actions')}
                    icon={<TestTube className="h-5 w-5" />}
                >
                    <div className="p-6 flex flex-wrap gap-3">
                        {currentSettings.ai_allowed_actions.slice(0, 3).map(action => (
                            <Button
                                key={action}
                                variant="outline"
                                onClick={() => onTestAction(action)}
                                disabled={isTesting}
                            >
                                {isTesting ? t('integrations.agentAi.testingAction', 'Testing...') : `Test: ${t(`integrations.agentAi.actions.${action}`, action)}`}
                            </Button>
                        ))}
                    </div>
                </FormBox>
            )}
        </div>
    );
};

export default AgentAiTab;
